/**
 * The usage trend — one chart for requests, one for tokens, side by side when
 * there is room and stacked when there is not.
 *
 * The reference (bimgo-ai-web `UsageTrendCharts`) draws this with Recharts.
 * This is one series of daily counts, so it is an inline `<svg>` instead: a
 * charting library's bundle and its CSP surface are a poor trade for a path and
 * five gridlines. What is copied is everything the reader sees, at the
 * reference's numbers — the 210/230px plot, the 32px (requests) and 48px
 * (tokens) value axis, five whole-number ticks, tokens as `500K` / `1.5M`,
 * `Sep 1` dates thinned the way Recharts' `preserveEnd` thins them, the dashed
 * horizontal grid, the monotone line, and on hover a cursor, a dot and a
 * tooltip with the full date and the exact value.
 *
 * The SVG is drawn in **CSS pixels**: its viewBox is the measured size of the
 * plot. A fixed viewBox stretched with `preserveAspectRatio="none"` distorted
 * every axis label — squashed on a narrow pane, smeared on a wide one.
 *
 * Requests and tokens are **separate charts**, as they are in the reference:
 * tokens run three or four orders of magnitude above requests, so a shared axis
 * flattens the request line onto the baseline and it reads as "no requests".
 */
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { dateOnlyToUtc, type UsagePoint } from "../../lib/usage";

/** The reference's `margin={{ top: 8, right: 10, left: 0, bottom: 0 }}`. */
const MARGIN = { top: 8, right: 10 };
/** Recharts' default x-axis height. */
const X_AXIS_H = 30;
/** Recharts' default tick size plus the reference's `tickMargin={8}`. */
const X_TICK_OFFSET = 6 + 8;
/** Recharts' default tick size plus its default tick margin on the y axis. */
const Y_TICK_OFFSET = 6 + 2;
/** The reference's `minTickGap={24}`. */
const MIN_TICK_GAP = 24;
const TICK_FONT_PX = 12;
/** Used until the first measurement lands. */
const FALLBACK_W = 480;

const COLOUR = {
  requests: "hsl(166 73% 35%)",
  tokens: "hsl(220 70% 50%)",
} as const;

const TICK_COUNT = 5;

/**
 * The value axis exactly as Recharts lays it out for the reference's
 * `[0, auto]` domain with `allowDecimals={false}` (recharts-scale
 * `getNiceTickValues`): five ticks from zero, the step rounded up to a
 * multiple of 0.05 x its power of ten (0.1 x for single digits) and then to a
 * whole number, and widened until five ticks reach the peak. So a 13.8M peak
 * reads 0 / 3.5M / 7.0M / 10.5M / 14.0M, as it does on the web.
 */
function valueTicks(peak: number): number[] {
  // Floating-point slack, so 0.35 / 0.05 is 7 and not 7.000000001.
  const EPS = 1e-9;
  const top = Math.max(peak, 1);
  const rough = top / (TICK_COUNT - 1);
  const digits = Math.floor(Math.log10(rough)) + 1;
  const power = 10 ** digits;
  const scale = digits !== 1 ? 0.05 : 0.1;
  for (let correction = 0; ; correction += 1) {
    const ratio = (Math.ceil(rough / power / scale - EPS) + correction) * scale;
    const step = Math.max(1, Math.ceil(ratio * power - EPS * power));
    if (Math.ceil(top / step - EPS) + 1 <= TICK_COUNT) {
      return Array.from({ length: TICK_COUNT }, (_, k) => k * step);
    }
  }
}

/** The reference's `formatTokenTick`. */
function tokenTick(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
  return String(value);
}

/**
 * d3's `curveMonotoneX`, which is what Recharts' `type="monotone"` draws: a
 * cubic that never overshoots a day's value, so a zero day is never drawn
 * dipping below the axis.
 */
function monotonePath(points: { x: number; y: number }[]): string {
  const n = points.length;
  if (n === 0) return "";
  const p = points;
  if (n === 1) return `M ${p[0]!.x} ${p[0]!.y}`;
  if (n === 2) return `M ${p[0]!.x} ${p[0]!.y} L ${p[1]!.x} ${p[1]!.y}`;
  const slope = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    b.x === a.x ? 0 : (b.y - a.y) / (b.x - a.x);
  const tangent: number[] = new Array(n).fill(0);
  for (let i = 1; i < n - 1; i += 1) {
    const h0 = p[i]!.x - p[i - 1]!.x;
    const h1 = p[i + 1]!.x - p[i]!.x;
    const s0 = slope(p[i - 1]!, p[i]!);
    const s1 = slope(p[i]!, p[i + 1]!);
    const mid = (s0 * h1 + s1 * h0) / (h0 + h1);
    tangent[i] =
      (Math.sign(s0) + Math.sign(s1)) *
        Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(mid)) || 0;
  }
  tangent[0] = (3 * slope(p[0]!, p[1]!) - tangent[1]!) / 2;
  tangent[n - 1] = (3 * slope(p[n - 2]!, p[n - 1]!) - tangent[n - 2]!) / 2;
  let d = `M ${p[0]!.x} ${p[0]!.y}`;
  for (let i = 0; i < n - 1; i += 1) {
    const a = p[i]!;
    const b = p[i + 1]!;
    const dx = (b.x - a.x) / 3;
    d += ` C ${a.x + dx} ${a.y + dx * tangent[i]!}, ${b.x - dx} ${b.y - dx * tangent[i + 1]!}, ${b.x} ${b.y}`;
  }
  return d;
}

let measureContext: CanvasRenderingContext2D | null | undefined;
/** A label's rendered width, so date ticks can be thinned before they collide. */
function textWidth(text: string, fontFamily: string): number {
  if (measureContext === undefined) {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  if (!measureContext) return text.length * TICK_FONT_PX * 0.6;
  measureContext.font = `${TICK_FONT_PX}px ${fontFamily}`;
  return measureContext.measureText(text).width;
}

/**
 * Recharts' default `interval="preserveEnd"`: walk back from the last tick,
 * keeping each one that clears the previously kept one by `MIN_TICK_GAP`. The
 * last tick is nudged inward if it would run past the right edge.
 */
function thinTicks(
  xs: readonly number[],
  widths: readonly number[],
  start: number,
  end: number,
): Map<number, number> {
  const kept = new Map<number, number>();
  let boundary = end;
  for (let i = xs.length - 1; i >= 0; i -= 1) {
    const isLast = i === xs.length - 1;
    const half = widths[i]! / 2;
    const x = isLast ? Math.min(xs[i]!, end - half) : xs[i]!;
    if (
      x + half + (isLast ? 0 : MIN_TICK_GAP) <= boundary &&
      x - half >= start
    ) {
      kept.set(i, x);
      boundary = x - half;
    }
  }
  return kept;
}

/** The plot's measured size and font, tracked as the pane resizes. */
function useBox() {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({
    width: FALLBACK_W,
    height: 210,
    font: "sans-serif",
  });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const width = Math.round(el.clientWidth);
      const height = Math.round(el.clientHeight);
      const font = getComputedStyle(el).fontFamily || "sans-serif";
      if (width > 0 && height > 0) setBox({ width, height, font });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, box] as const;
}

function Chart({
  points,
  field,
  label,
  locale,
  dateLabel,
}: {
  points: readonly UsagePoint[];
  field: "requests" | "tokens";
  label: string;
  locale: string;
  dateLabel: string;
}) {
  const [ref, { width: W, height: H, font }] = useBox();
  const [hover, setHover] = useState<{
    index: number;
    x: number;
    y: number;
  } | null>(null);

  const dateLocale = locale === "zh" ? "zh-TW" : "en-US";
  // A point's date is a **calendar day**, not an instant. Read as one,
  // `"2026-09-01"` is UTC midnight — the previous day anywhere west of
  // Greenwich. The reference pins these to UTC noon and formats in UTC.
  const shortDate = (iso: string) =>
    dateOnlyToUtc(iso).toLocaleDateString(dateLocale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  const fullDate = (iso: string) =>
    dateOnlyToUtc(iso).toLocaleDateString(dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });

  const values = points.map((p) => p[field]);
  const ticks = valueTicks(Math.max(...values, 0));
  const max = ticks[ticks.length - 1]!;
  const yAxisW = field === "tokens" ? 48 : 32;
  const tickText = field === "tokens" ? tokenTick : String;

  const left = yAxisW;
  const right = W - MARGIN.right;
  const top = MARGIN.top;
  const bottom = H - X_AXIS_H;
  const plotW = Math.max(1, right - left);

  const xOf = (i: number) =>
    left + (points.length <= 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const yOf = (v: number) => bottom - (v / max) * (bottom - top);
  const coords = values.map((v, i) => ({ x: xOf(i), y: yOf(v) }));

  const dateTicks = points.map((p) => shortDate(p.date));
  const kept = thinTicks(
    coords.map((c) => c.x),
    dateTicks.map((text) => textWidth(text, font)),
    0,
    W,
  );

  const onMove = (event: PointerEvent<SVGSVGElement>) => {
    if (points.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    const at =
      points.length <= 1
        ? 0
        : Math.round(((x - left) / plotW) * (points.length - 1));
    setHover({ index: Math.min(points.length - 1, Math.max(0, at)), x, y });
  };

  const active = hover ? points[hover.index] : undefined;
  const colour = COLOUR[field];

  return (
    <figure className="trend">
      <figcaption className="trend-label">{label}</figcaption>
      <div className="trend-plot" ref={ref}>
        <svg
          className="trend-svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={label}
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map((value) => {
            const y = yOf(value);
            return (
              <g key={value}>
                <line
                  className="trend-grid"
                  x1={left}
                  x2={right}
                  y1={y}
                  y2={y}
                />
                <text
                  className="trend-tick"
                  x={left - Y_TICK_OFFSET}
                  y={y}
                  dy="0.355em"
                  textAnchor="end"
                >
                  {tickText(value)}
                </text>
              </g>
            );
          })}
          {[...kept].map(([i, x]) => (
            <text
              key={`t-${points[i]!.date}`}
              className="trend-tick"
              x={x}
              y={bottom + X_TICK_OFFSET}
              dy="0.71em"
              textAnchor="middle"
            >
              {dateTicks[i]}
            </text>
          ))}
          {hover && (
            <line
              className="trend-cursor"
              x1={coords[hover.index]!.x}
              x2={coords[hover.index]!.x}
              y1={top}
              y2={bottom}
            />
          )}
          <path
            className="trend-line"
            d={monotonePath(coords)}
            style={{ stroke: colour }}
          />
          {hover && (
            <circle
              className="trend-dot"
              cx={coords[hover.index]!.x}
              cy={coords[hover.index]!.y}
              r={4}
              style={{ fill: colour }}
            />
          )}
        </svg>
        {hover && active && (
          <div
            className="trend-tooltip"
            style={{
              // Recharts' 10px offset from the pointer, flipped to the left
              // when it would run off the chart.
              left: hover.x + 10,
              top: hover.y + 10,
              transform:
                hover.x > W / 2 ? "translateX(calc(-100% - 20px))" : undefined,
            }}
          >
            <span
              className="trend-tooltip-bar"
              style={{ background: colour }}
            />
            <span>
              <span className="trend-tooltip-date">
                {fullDate(active.date)}
              </span>
              <span className="trend-tooltip-value">
                {active[field].toLocaleString(dateLocale)}
              </span>
            </span>
          </div>
        )}
      </div>
      <div className="visually-hidden">
        <table>
          <caption>{label}</caption>
          <thead>
            <tr>
              <th>{dateLabel}</th>
              <th>{label}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.date}>
                <td>{fullDate(p.date)}</td>
                <td>{p[field].toLocaleString(dateLocale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}

/**
 * Two charts, or the empty line.
 *
 * `points` rather than a whole timeseries: the org analytics answers with its
 * own point shape and would otherwise need a fake `timezone`/`days` wrapper
 * built around it just to reach this component.
 *
 * **A run of zeroes is empty, not a flat line at the baseline.** The reference
 * tests `some(requests > 0 || tokens > 0)`, not `points.length`: a range the
 * server filled with thirty zero-days is not "no data", it is "no activity",
 * and drawing it as a line pinned to the axis says the same thing far less
 * clearly than the sentence does.
 */
export function UsageTrend({
  points,
  tokenLabel,
  emptyLabel,
}: {
  points: readonly UsagePoint[];
  /** "Tokens" on the personal trend, "Token" on the org analytics. */
  tokenLabel?: string;
  emptyLabel?: string;
}) {
  const { t, locale } = useLocale();

  const hasActivity = points.some((p) => p.requests > 0 || p.tokens > 0);
  if (!hasActivity) {
    return (
      <p className="trend-empty">{emptyLabel ?? t("dashboard.chart.empty")}</p>
    );
  }

  return (
    // The outer box is the size container: a container query cannot restyle
    // the element it measures, so the grid sits one level in.
    <div className="trend-box">
      <div className="trend-group">
        <Chart
          points={points}
          field="requests"
          label={t("dashboard.chart.requests")}
          locale={locale}
          dateLabel={t("dashboard.analytics.date")}
        />
        <Chart
          points={points}
          field="tokens"
          label={tokenLabel ?? t("dashboard.chart.tokens")}
          locale={locale}
          dateLabel={t("dashboard.analytics.date")}
        />
      </div>
    </div>
  );
}
